import { type RouteObject, Navigate, Outlet } from 'react-router-dom'
import { getRouteComponent } from './routeRegistry'
import { AuthGuard } from '@/components/auth/AuthGuard'
import { AdminLayout } from '@/components/layout/AdminLayout'
interface UserRoute {
  id?: number
  path: string
  name: string
  icon?: string
  side_nav?: string
  children?: UserRoute[]
}

interface GenerateRoutesOptions {
  includeFallback?: boolean
}

/**
 * Generate React Router routes from user_routes (database navigation)
 */
export function generateRoutesFromUserRoutes(
  userRoutes: UserRoute[],
  publicRoutes: RouteObject[] = [],
  options: GenerateRoutesOptions = {}
): RouteObject[] {
  const routes: RouteObject[] = [
    // Public routes (always available)
    ...publicRoutes,
    {
      path: '/',
      element: <Navigate to="/dashboard" replace />,
    },
  ]

  const protectedRoutes: RouteObject[] = []

  /**
   * Recursively process routes and their children
   */
  function processRoute(route: UserRoute, parentPath: string = '') {
    let fullPath: string

    if (route.path.startsWith('/')) {
      // Absolute path - check if it exists in route registry
      // If not, try joining with parentPath (for cases like /users under /user-management)
      const absolutePath = route.path
      const ComponentForAbsolute = getRouteComponent(absolutePath)

      if (ComponentForAbsolute) {
        // Absolute path exists in registry (e.g., /shipping-lines/create)
        fullPath = absolutePath
      } else if (parentPath) {
        // Absolute path doesn't exist, try joining with parent
        // (e.g. backend sends child path "/service-types" under "/service-catalog" -> /service-catalog/service-types)
        const joined =
          parentPath.replace(/\/+$/, '') + '/' + absolutePath.replace(/^\/+/, '')
        const joinedPath = joined.startsWith('/') ? joined : '/' + joined
        const ComponentForJoined = getRouteComponent(joinedPath)

        if (ComponentForJoined) {
          fullPath = joinedPath
        } else {
          // Neither absolute nor joined path exists, use absolute as fallback
          fullPath = absolutePath
        }
      } else {
        // No parent path, use absolute as-is
        fullPath = absolutePath
      }
    } else {
      // Relative path - join with parentPath (use slash like Sidebar joinPaths)
      const joined = parentPath
        ? `${parentPath.replace(/\/+$/, '')}/${route.path.replace(/^\/+/, '')}`
        : route.path
      fullPath = joined.startsWith('/') ? joined : `/${joined}`
    }

    // Get component for this route (registry keys use leading slash)
    const Component = getRouteComponent(fullPath)
    const alreadyRegistered = protectedRoutes.some((r) => r.path === fullPath)

    if (Component && !alreadyRegistered) {
      protectedRoutes.push({
        path: fullPath,
        element: <Component />,
      })
    }

    // Process children using fullPath as parent context
    if (route.children && route.children.length > 0) {
      route.children.forEach((child) => processRoute(child, fullPath))
    }
  }

  // Process all user routes
  userRoutes.forEach((route) => processRoute(route))

  // Always ensure dashboard route exists (even if user_routes is empty or doesn't include it)
  const hasDashboard = protectedRoutes.some((r) => r.path === '/dashboard')
  if (!hasDashboard) {
    const DashboardComponent = getRouteComponent('/dashboard')
    if (DashboardComponent) {
      protectedRoutes.push({
        path: '/dashboard',
        element: <DashboardComponent />,
      })
    }
  }

  // Always ensure parent ticket routes exist (may not be in user_routes from backend)
  const ticketPaths = ['/tickets', '/tickets/create', '/tickets/:id/edit', '/tickets/:id/view']
  for (const path of ticketPaths) {
    if (!protectedRoutes.some((r) => r.path === path)) {
      const Component = getRouteComponent(path)
      if (Component) {
        protectedRoutes.push({
          path,
          element: <Component />,
        })
      }
    }
  }

  // Always ensure agent status route exists (linked from header / sidebar)
  const agentStatusPath = '/agent-status'
  if (!protectedRoutes.some((r) => r.path === agentStatusPath)) {
    const AgentStatusComponent = getRouteComponent(agentStatusPath)
    if (AgentStatusComponent) {
      protectedRoutes.push({
        path: agentStatusPath,
        element: <AgentStatusComponent />,
      })
    }
  }

  // Report pages (dashboard quick-links; may not yet be in user_routes until nav is re-seeded)
  const reportPaths = [
    '/reports/overview',
    '/reports/analytics',
    '/reports/sla-compliance',
    '/reports/agent-performance',
    '/reports/ticket-volume',
    '/reports/historical-export',
    '/reports/task-reports',
  ]
  for (const path of reportPaths) {
    if (!protectedRoutes.some((r) => r.path === path)) {
      const Component = getRouteComponent(path)
      if (Component) {
        protectedRoutes.push({
          path,
          element: <Component />,
        })
      }
    }
  }

  const feFallbackPaths = [
    '/field-engineers/schedule',
    '/field-engineers/live-map',
  ]
  for (const path of feFallbackPaths) {
    if (!protectedRoutes.some((r) => r.path === path)) {
      const Component = getRouteComponent(path)
      if (Component) {
        protectedRoutes.push({
          path,
          element: <Component />,
        })
      }
    }
  }

  // Always ensure My Tasks route is accessible
  const myTasksPath = '/my-tasks'
  if (!protectedRoutes.some((r) => r.path === myTasksPath)) {
    const MyTasksComponent = getRouteComponent(myTasksPath)
    if (MyTasksComponent) {
      protectedRoutes.push({
        path: myTasksPath,
        element: <MyTasksComponent />,
      })
    }
  }

  // Help pages (pinned in sidebar; always available regardless of user_routes)
  for (const helpPath of ['/user-manual', '/faq']) {
    if (!protectedRoutes.some((r) => r.path === helpPath)) {
      const HelpComponent = getRouteComponent(helpPath)
      if (HelpComponent) {
        protectedRoutes.push({
          path: helpPath,
          element: <HelpComponent />,
        })
      }
    }
  }

  // Service Catalog create/edit paths (list pages come from user_routes; nested CRUD often does not)
  const catalogCrudBases = [
    '/service-catalog/service-types',
    '/service-catalog/parent-ticket-statuses',
    '/service-catalog/agent-availability-statuses',
    '/service-catalog/child-ticket-statuses',
    '/service-catalog/sla-timing',
    '/service-catalog/support-types',
    '/service-catalog/task-templates',
    '/service-catalog/issue-categories',
  ]
  for (const base of catalogCrudBases) {
    for (const suffix of ['/create', '/:id']) {
      const path = `${base}${suffix}`
      if (!protectedRoutes.some((r) => r.path === path)) {
        const Component = getRouteComponent(path)
        if (Component) {
          protectedRoutes.push({
            path,
            element: <Component />,
          })
        }
      }
    }
  }

  // Fallback to dashboard for any unmatched route (only after initialization)
  const includeFallback = options.includeFallback ?? true
  if (includeFallback && protectedRoutes.length > 0) {
    protectedRoutes.push({
      path: '*',
      element: <Navigate to="/dashboard" replace />,
    })
  }

  if (protectedRoutes.length > 0) {
    routes.push({
      element: (
        <AuthGuard>
          <AdminLayout><Outlet /></AdminLayout>
        </AuthGuard>
      ),
      children: protectedRoutes,
    })
  }

  return routes
}
